import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Skeleton,
} from '@mui/material';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CircleIcon from '@mui/icons-material/Circle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import { bloomberg } from '../theme/bloombergTheme';
import api from '../services/api';

interface StockAlert {
  id: number;
  name: string | null;
  alert_type: string;
  symbol: string | null;
  threshold: number | null;
  screener_params: Record<string, any> | null;
  is_active: boolean;
  is_recurring: boolean;
  created_at: string;
}

interface TriggeredAlert {
  id: number;
  alert_id: number;
  symbol: string;
  message: string;
  current_value: number | null;
  is_read: boolean;
  triggered_at: string;
}

const ALERT_TYPE_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  price_above:        { label: 'Price Above',      color: bloomberg.status.positive, icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  price_below:        { label: 'Price Below',      color: bloomberg.status.negative, icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  rsi_above:          { label: 'RSI Above',         color: bloomberg.status.warning,  icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  rsi_below:          { label: 'RSI Below',         color: bloomberg.accent.blue,     icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  pct_change_up:      { label: '1D Move ▲',         color: bloomberg.status.positive, icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  pct_change_down:    { label: '1D Move ▼',         color: bloomberg.status.negative, icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  above_ma20:         { label: 'Cross 20MA ▲',      color: bloomberg.status.warning,  icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  below_ma20:         { label: 'Cross 20MA ▼',      color: bloomberg.accent.blue,     icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  above_ma50:         { label: 'Cross 50MA ▲',      color: bloomberg.status.warning,  icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  below_ma50:         { label: 'Cross 50MA ▼',      color: bloomberg.accent.blue,     icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  above_ma200:        { label: 'Cross 200MA ▲',     color: bloomberg.status.warning,  icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  below_ma200:        { label: 'Cross 200MA ▼',     color: bloomberg.accent.blue,     icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  macd_cross_bullish: { label: 'MACD Bull Cross',   color: bloomberg.status.positive, icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
  macd_cross_bearish: { label: 'MACD Bear Cross',   color: bloomberg.status.negative, icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  screener:           { label: 'Screener',           color: bloomberg.accent.cyan,     icon: <FilterListIcon sx={{ fontSize: 14 }} /> },
};

const TYPES_WITH_THRESHOLD = new Set(['price_above', 'price_below', 'rsi_above', 'rsi_below', 'pct_change_up', 'pct_change_down']);
const TYPES_WITH_SYMBOL = new Set([
  'price_above', 'price_below', 'rsi_above', 'rsi_below',
  'pct_change_up', 'pct_change_down',
  'above_ma20', 'below_ma20', 'above_ma50', 'below_ma50', 'above_ma200', 'below_ma200',
  'macd_cross_bullish', 'macd_cross_bearish',
]);

const Notifications: React.FC = () => {
  const [alerts, setAlerts] = useState<StockAlert[]>([]);
  const [triggered, setTriggered] = useState<TriggeredAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [formType, setFormType] = useState('price_below');
  const [formSymbol, setFormSymbol] = useState('');
  const [formThreshold, setFormThreshold] = useState('');
  const [formName, setFormName] = useState('');
  const [formRecurring, setFormRecurring] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, triggeredRes] = await Promise.all([
        api.get(`/api/alerts`),
        api.get(`/api/alerts/triggered`),
      ]);
      setAlerts(alertsRes.data);
      setTriggered(triggeredRes.data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleCreateAlert = async () => {
    try {
      const needsSymbol = TYPES_WITH_SYMBOL.has(formType);
      const needsThreshold = TYPES_WITH_THRESHOLD.has(formType);
      if (needsSymbol && !formSymbol) {
        setSnackbar({ open: true, message: 'Symbol is required', severity: 'error' });
        return;
      }
      if (needsThreshold && !formThreshold) {
        setSnackbar({ open: true, message: 'Threshold is required', severity: 'error' });
        return;
      }
      const payload: any = {
        alert_type: formType,
        symbol: needsSymbol ? formSymbol.toUpperCase() : null,
        threshold: needsThreshold ? parseFloat(formThreshold) : null,
        name: formName || null,
        is_recurring: formRecurring,
      };

      await api.post(`/api/alerts`, payload);
      setSnackbar({ open: true, message: 'Alert created', severity: 'success' });
      setDialogOpen(false);
      setFormSymbol('');
      setFormThreshold('');
      setFormName('');
      setFormRecurring(false);
      fetchAlerts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Error creating alert', severity: 'error' });
    }
  };

  const handleToggleActive = async (id: number, currentActive: boolean) => {
    try {
      await api.put(`/api/alerts/${id}`, { is_active: !currentActive });
      fetchAlerts();
    } catch (err) {
      console.error('Error toggling alert:', err);
    }
  };

  const handleDeleteAlert = async (id: number) => {
    try {
      await api.delete(`/api/alerts/${id}`);
      fetchAlerts();
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  const handleMarkRead = async (id: number) => {
    try {
      await api.put(`/api/alerts/triggered/${id}/read`);
      setTriggered((prev) => prev.map((t) => (t.id === id ? { ...t, is_read: true } : t)));
    } catch (err) {
      console.error('Error marking read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put(`/api/alerts/triggered/read-all`);
      setTriggered((prev) => prev.map((t) => ({ ...t, is_read: true })));
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  const unreadCount = triggered.filter((t) => !t.is_read).length;

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = now.getTime() - d.getTime();
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: bloomberg.accent.orange }}>
        Alerts
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {/* Left panel: Manage alerts */}
        <Paper sx={{ flex: '1 1 400px', p: 2.5, minWidth: 350 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddAlertIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
                My Alerts
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddAlertIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ fontSize: 11 }}
            >
              New Alert
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={48} />)}
            </Box>
          ) : alerts.length === 0 ? (
            <Typography variant="body2" sx={{ color: bloomberg.text.muted, textAlign: 'center', py: 4 }}>
              No alerts configured. Create one to get started.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {alerts.map((alert) => {
                const typeInfo = ALERT_TYPE_LABELS[alert.alert_type] || ALERT_TYPE_LABELS.price_below;
                return (
                  <Box
                    key={alert.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${bloomberg.border.primary}`,
                      bgcolor: alert.is_active ? 'transparent' : 'rgba(100,100,100,0.05)',
                      opacity: alert.is_active ? 1 : 0.5,
                      '&:hover': { bgcolor: bloomberg.bg.hover },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: typeInfo.color }}>
                      {typeInfo.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {alert.name || `${alert.symbol} ${typeInfo.label}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontSize: 10 }}>
                        {alert.symbol && `${alert.symbol} `}
                        {alert.threshold != null && `@ ${alert.threshold}`}
                        {alert.is_recurring && ' | Recurring'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={typeInfo.label}
                      sx={{
                        height: 18,
                        fontSize: 9,
                        fontWeight: 700,
                        bgcolor: `${typeInfo.color}20`,
                        color: typeInfo.color,
                      }}
                    />
                    <Tooltip title={alert.is_active ? 'Active' : 'Inactive'}>
                      <Switch
                        size="small"
                        checked={alert.is_active}
                        onChange={() => handleToggleActive(alert.id, alert.is_active)}
                      />
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleDeleteAlert(alert.id)} sx={{ color: bloomberg.text.muted }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* Right panel: Triggered alerts inbox */}
        <Paper sx={{ flex: '1 1 500px', p: 2.5, minWidth: 350 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsActiveIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
                Triggered
              </Typography>
              {unreadCount > 0 && (
                <Chip
                  size="small"
                  label={unreadCount}
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: bloomberg.status.negativeBg,
                    color: bloomberg.status.negative,
                    ml: 0.5,
                  }}
                />
              )}
            </Box>
            {unreadCount > 0 && (
              <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead} sx={{ fontSize: 11 }}>
                Mark All Read
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={40} />)}
            </Box>
          ) : triggered.length === 0 ? (
            <Typography variant="body2" sx={{ color: bloomberg.text.muted, textAlign: 'center', py: 4 }}>
              No triggered alerts yet. They will appear here when conditions are met.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 600, overflow: 'auto' }}>
              {triggered.map((t) => (
                <Box
                  key={t.id}
                  onClick={() => !t.is_read && handleMarkRead(t.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${t.is_read ? bloomberg.border.primary : bloomberg.accent.orange}40`,
                    bgcolor: t.is_read ? 'transparent' : bloomberg.accent.orangeDim,
                    cursor: t.is_read ? 'default' : 'pointer',
                    '&:hover': { bgcolor: bloomberg.bg.hover },
                  }}
                >
                  {!t.is_read && (
                    <CircleIcon sx={{ fontSize: 8, color: bloomberg.accent.orange }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={t.symbol}
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: bloomberg.accent.orangeDim,
                          color: bloomberg.accent.orange,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.message}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontSize: 10, whiteSpace: 'nowrap' }}>
                    {formatTime(t.triggered_at)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Create Alert Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Alert</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Alert Name (optional)"
              size="small"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g., AAPL below $150"
            />
            <FormControl size="small">
              <InputLabel>Alert Type</InputLabel>
              <Select
                value={formType}
                label="Alert Type"
                onChange={(e) => setFormType(e.target.value)}
              >
                <MenuItem disabled sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700, opacity: '1 !important' }}>— Price —</MenuItem>
                <MenuItem value="price_above">Price Above</MenuItem>
                <MenuItem value="price_below">Price Below</MenuItem>
                <MenuItem disabled sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700, opacity: '1 !important' }}>— 1-Day Move —</MenuItem>
                <MenuItem value="pct_change_up">1D Move Up ▲</MenuItem>
                <MenuItem value="pct_change_down">1D Move Down ▼</MenuItem>
                <MenuItem disabled sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700, opacity: '1 !important' }}>— RSI —</MenuItem>
                <MenuItem value="rsi_above">RSI Above</MenuItem>
                <MenuItem value="rsi_below">RSI Below</MenuItem>
                <MenuItem disabled sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700, opacity: '1 !important' }}>— Moving Averages —</MenuItem>
                <MenuItem value="above_ma20">Cross 20MA ▲</MenuItem>
                <MenuItem value="below_ma20">Cross 20MA ▼</MenuItem>
                <MenuItem value="above_ma50">Cross 50MA ▲</MenuItem>
                <MenuItem value="below_ma50">Cross 50MA ▼</MenuItem>
                <MenuItem value="above_ma200">Cross 200MA ▲</MenuItem>
                <MenuItem value="below_ma200">Cross 200MA ▼</MenuItem>
                <MenuItem disabled sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 700, opacity: '1 !important' }}>— MACD —</MenuItem>
                <MenuItem value="macd_cross_bullish">MACD Bull Cross</MenuItem>
                <MenuItem value="macd_cross_bearish">MACD Bear Cross</MenuItem>
              </Select>
            </FormControl>
            {TYPES_WITH_SYMBOL.has(formType) && (
              <TextField
                label="Symbol"
                size="small"
                value={formSymbol}
                onChange={(e) => setFormSymbol(e.target.value.toUpperCase())}
                placeholder="e.g., AAPL"
              />
            )}
            {TYPES_WITH_THRESHOLD.has(formType) && (
              <TextField
                label={formType.includes('pct_change') ? '% Threshold' : 'Threshold'}
                size="small"
                type="number"
                value={formThreshold}
                onChange={(e) => setFormThreshold(e.target.value)}
                placeholder={
                  formType.includes('rsi') ? 'e.g., 30' :
                  formType.includes('pct_change') ? 'e.g., 2  (≥2% move)' :
                  'e.g., 150.00'
                }
                helperText={formType.includes('pct_change') ? 'Fires when stock moves ≥ this % in 1 day' : undefined}
              />
            )}
            <FormControlLabel
              control={<Switch checked={formRecurring} onChange={(_, v) => setFormRecurring(v)} size="small" />}
              label={<Typography variant="body2">Recurring (re-arm after trigger)</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleCreateAlert} variant="contained" size="small">Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
